import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { ButtonLink } from './shared/ButtonLink';
import type { NavbarComponent as NavbarComponentType } from '../schemas/validation';
import { usePreviewContext } from '../contexts/PreviewContext';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { clampLogoImageSizeForRender } from './LogoUploadModal/useLogoUpload';
import { isLegacyBothLogo } from '../utils/navbarLogo';
import { previewNavbarUrl, resolveNavbarLinkUrl, uniqueNavbarLinks } from '../utils/navbarLinks';

interface NavbarComponentProps {
  component: NavbarComponentType;
}

export const NavbarComponent: React.FC<NavbarComponentProps> = ({ component }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const { data: rawData } = component;
  const { isPreviewMode, siteId } = usePreviewContext();
  const { currentTheme } = useThemeSafe();

  // Normalize data to handle alternative formats (e.g., from Roary AI)
  // Roary may create navbar components with different field names
  const normalizeData = (d: typeof rawData) => {
    const normalized = { ...d };

    // Handle alternative field names for siteName
    if (!normalized.siteName) {
      const legacyData = d as any;
      normalized.siteName = legacyData.brandName || legacyData.name || legacyData.title || '';
    }

    // Handle alternative field names for links/navigation
    if (!normalized.links || normalized.links.length === 0) {
      const legacyData = d as any;

      // Check for alternative navigation array names
      const navItems = legacyData.navigation || legacyData.navItems || legacyData.menuItems || [];

      if (navItems.length > 0) {
        normalized.links = navItems.map((item: any, index: number) => ({
          text: item.text || item.label || item.title || item.name || '',
          url: item.url || item.href || item.link || item.path || '/',
          order: item.order ?? index,
          style: item.style || 'normal',
        }));
      }
    }

    // Default alignment if not provided
    if (!normalized.alignment) {
      normalized.alignment = 'right';
    }

    return normalized;
  };

  const data = normalizeData(rawData);

  // Get custom colors from context
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  const brandFonts = useBrandFonts();

  // Font styles for body text (navbar has no headings)
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const getNavigationUrl = (url: string) => previewNavbarUrl(url, { isPreviewMode, siteId });

  // Alignment classes for the navigation links only
  const linkAlignmentClasses = {
    left: 'ml-10',
    center: 'mx-auto',
    right: 'ml-auto',
    'space-between': 'ml-auto', // For space-between, put links on the right
  };

  const linkContainerClasses = {
    left: 'flex items-baseline space-x-4',
    center: 'flex items-baseline space-x-4',
    right: 'flex items-baseline space-x-4',
    'space-between': 'flex items-baseline space-x-4',
  };

  // Check if we have custom colors that are hex values
  const isHexColor = (color: string | undefined) => color?.startsWith('#');

  // Build navbar styles - use inline styles for hex colors, classes for Tailwind
  const navbarStyle: React.CSSProperties = {};
  const navbarClasses: string[] = [];

  // Note: sticky positioning is now handled by the ComponentRenderer wrapper div
  // to ensure proper sticky behavior. The z-index is kept here for proper layering.
  if (data.sticky !== false) {
    navbarClasses.push('z-50');
  }

  // Color priority: customColors (user explicit) > theme/brand package > stored data defaults
  // Handle background color
  const backgroundColor =
    data.customColors?.background || themeStyles.navbar?.background || data.backgroundColor;
  if (backgroundColor && isHexColor(backgroundColor)) {
    navbarStyle.backgroundColor = backgroundColor;
  } else if (backgroundColor) {
    navbarClasses.push(backgroundColor);
  }

  // Handle general text color
  const textColor = data.customColors?.text || themeStyles.navbar?.text || data.textColor;
  if (textColor) {
    if (isHexColor(textColor)) {
      navbarStyle.color = textColor;
    } else {
      navbarClasses.push(textColor);
    }
  }

  // Add other classes (only if they're not color-related)
  if (themeStyles.navbar?.shadow && !isHexColor(themeStyles.navbar?.shadow)) {
    navbarClasses.push(themeStyles.navbar?.shadow);
  }
  if (themeStyles.navbar?.border && !isHexColor(themeStyles.navbar?.border)) {
    navbarClasses.push(themeStyles.navbar?.border);
  }
  if (themeStyles.navbar?.height) navbarClasses.push(themeStyles.navbar?.height);
  navbarClasses.push('transition-all duration-200');

  const navClasses = navbarClasses.join(' ');

  const sortedLinks = uniqueNavbarLinks([...(data.links || [])].sort((a, b) => a.order - b.order));

  // Resolve link hover color at component level for CSS <style> tag (SSR-safe)
  const navLinkHoverColor = data.customColors?.linkHover || themeStyles.navbar?.linkHover || '';

  // Get link styling based on style type (normal, button, outline)
  const getLinkStyles = (linkStyle: string = 'normal', isMobile: boolean = false) => {
    const linkColor =
      data.customColors?.linkText || themeStyles.navbar?.linkText || themeStyles.navbar?.text;
    const linkHover = navLinkHoverColor;

    // Button styles use a contrasting color approach
    const buttonBg = themeStyles.navbar?.buttonBg || '#3b82f6'; // Default blue if not in theme
    const buttonText = themeStyles.navbar?.buttonText || '#ffffff';
    const buttonHoverBg = themeStyles.navbar?.buttonHoverBg || '#2563eb';

    const baseStyle: React.CSSProperties = {};
    let baseClass = '';

    switch (linkStyle) {
      case 'button':
        // Solid button style
        baseClass = isMobile
          ? 'block w-full text-center px-4 py-3 rounded-lg text-base font-semibold transition-all'
          : 'px-4 py-2 rounded-lg text-sm font-semibold transition-all';
        if (isHexColor(buttonBg)) {
          baseStyle.backgroundColor = buttonBg;
        } else {
          baseClass += ` ${buttonBg || 'bg-blue-600'}`;
        }
        if (isHexColor(buttonText)) {
          baseStyle.color = buttonText;
        } else {
          baseClass += ` ${buttonText || 'text-white'}`;
        }
        baseClass += ' hover:opacity-90 hover:scale-105';
        return { style: baseStyle, className: baseClass, isButton: true, buttonHoverBg };

      case 'outline':
        // Outline button style
        baseClass = isMobile
          ? 'nav-hover-link block w-full text-center px-4 py-3 rounded-lg text-base font-semibold transition-all border-2'
          : 'nav-hover-link px-4 py-2 rounded-lg text-sm font-semibold transition-all border-2';
        if (isHexColor(linkColor)) {
          baseStyle.color = linkColor;
          baseStyle.borderColor = linkColor;
        } else {
          baseClass += ` ${linkColor || 'text-gray-700'} border-current`;
        }
        baseClass += ' hover:opacity-80 bg-transparent';
        return { style: baseStyle, className: baseClass, isButton: false, linkColor, linkHover };

      default:
        // Normal link style (existing behavior)
        baseClass = isMobile
          ? 'nav-hover-link block px-3 py-2 rounded-md text-base font-medium transition-all'
          : 'nav-hover-link px-3 py-2 rounded-md text-sm font-medium transition-all';
        if (isHexColor(linkColor)) {
          baseStyle.color = linkColor;
        } else if (linkColor) {
          baseClass += ` ${linkColor}`;
        }
        if (!isHexColor(linkHover) && linkHover) {
          baseClass += ` ${linkHover}`;
        }
        baseClass += isHexColor(linkHover) || !linkHover ? ' hover:opacity-80' : '';
        return { style: baseStyle, className: baseClass, isButton: false, linkColor, linkHover };
    }
  };

  // Logo/brand rendering logic
  const renderLogo = () => {
    const logo = data.logo;
    const siteName = data.siteName;

    // If no logo configured, use siteName
    if (!logo && siteName) {
      const brandColor =
        data.customColors?.brandText || themeStyles.navbar?.brandText || themeStyles.navbar?.text;
      const brandStyle: React.CSSProperties = isHexColor(brandColor) ? { color: brandColor } : {};
      const brandClass = isHexColor(brandColor)
        ? 'text-xl font-bold transition-colors'
        : `text-xl font-bold ${brandColor} transition-colors`;

      return (
        <Link
          to={getNavigationUrl('/')}
          className={brandClass}
          style={{ ...brandStyle, ...bodyFontStyle }}
        >
          {siteName}
        </Link>
      );
    }

    if (!logo) return null;

    const LogoWrapper = logo.linkUrl ? 'a' : 'div';
    const logoProps = logo.linkUrl
      ? {
          href: getNavigationUrl(logo.linkUrl),
          target: logo.linkTarget || '_self',
          className: 'flex items-center hover:opacity-80 transition-opacity',
        }
      : {
          className: 'flex items-center',
        };

    // Size classes for text
    const textSizeClasses = {
      small: 'text-lg',
      medium: 'text-xl',
      large: 'text-2xl',
      xl: 'text-3xl',
    };

    // Weight classes for text
    const textWeightClasses = {
      normal: 'font-normal',
      bold: 'font-bold',
      light: 'font-light',
    };

    const logoTextColor =
      data.customColors?.brandText ||
      logo.textColor ||
      themeStyles.navbar?.brandText ||
      themeStyles.navbar?.text ||
      '#000000';

    const logoTextStyle: React.CSSProperties = {};
    if (isHexColor(logoTextColor)) {
      logoTextStyle.color = logoTextColor;
    }

    const legacyBoth = isLegacyBothLogo(logo);
    const showImage =
      (logo.mode === 'image' || legacyBoth) && (logo.imageUrl || logo.cloudfrontUrl);
    const showText = (logo.mode === 'text' || legacyBoth) && logo.text;
    const imageSizeMode = legacyBoth ? 'legacy-both' : 'image';
    const textSizeKey = legacyBoth ? 'medium' : logo.textSize || 'medium';
    const legacyTextExtras = legacyBoth ? ' truncate max-w-[180px] !text-base' : '';

    const textClass = isHexColor(logoTextColor)
      ? `${textSizeClasses[textSizeKey]} ${textWeightClasses[logo.textWeight || 'bold']} hover:opacity-80 transition-opacity${legacyTextExtras}`
      : `${textSizeClasses[textSizeKey]} ${textWeightClasses[logo.textWeight || 'bold']} ${logoTextColor} hover:opacity-80 transition-opacity${legacyTextExtras}`;

    return (
      <LogoWrapper {...logoProps}>
        <div
          className={`flex items-center min-w-0 max-h-14 overflow-hidden${legacyBoth ? ' flex-row gap-2' : ''}`}
        >
          {showImage &&
            (() => {
              const { width, height, maxWidth, maxHeight } = clampLogoImageSizeForRender(
                logo.imageWidth,
                logo.imageHeight,
                imageSizeMode
              );

              return (
                <img
                  src={logo.cloudfrontUrl || logo.imageUrl}
                  alt={logo.imageAlt || 'Logo'}
                  className="object-contain flex-shrink-0"
                  style={{
                    width: `${width}px`,
                    height: `${height}px`,
                    maxWidth: `${maxWidth}px`,
                    maxHeight: `${maxHeight}px`,
                  }}
                />
              );
            })()}

          {showText && (
            <span className={textClass} style={{ ...logoTextStyle, ...bodyFontStyle }}>
              {logo.text}
            </span>
          )}
        </div>
      </LogoWrapper>
    );
  };

  return (
    <>
      {navLinkHoverColor && isHexColor(navLinkHoverColor) && (
        <style>{`
          [data-widget="navbar"] .nav-hover-link:hover { color: ${navLinkHoverColor} !important; opacity: 1 !important; text-decoration: none !important; }
        `}</style>
      )}
      <header className={navClasses} style={navbarStyle} data-widget="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            {/* Logo/Brand - Always on the left */}
            <div className="flex-shrink-0 flex items-center max-h-16 min-w-0">{renderLogo()}</div>

            {/* Desktop Navigation - Positioned according to alignment setting */}
            <div className={`hidden md:block ${linkAlignmentClasses[data.alignment]}`}>
              <div className={`${linkContainerClasses[data.alignment]} gap-2`}>
                {sortedLinks.map((link, index) => {
                  const linkStyleConfig = getLinkStyles(link.style || 'normal', false);
                  const resolvedUrl = resolveNavbarLinkUrl(link, sortedLinks);
                  const navUrl = getNavigationUrl(resolvedUrl);

                  return (
                    <ButtonLink
                      key={index}
                      url={navUrl}
                      target={link.target}
                      className={linkStyleConfig.className}
                      style={{ ...linkStyleConfig.style, ...bodyFontStyle }}
                      onMouseEnter={e => {
                        if (linkStyleConfig.isButton && linkStyleConfig.buttonHoverBg) {
                          if (isHexColor(linkStyleConfig.buttonHoverBg)) {
                            e.currentTarget.style.backgroundColor = linkStyleConfig.buttonHoverBg;
                          }
                        } else if (
                          isHexColor(linkStyleConfig.linkHover) &&
                          linkStyleConfig.linkHover
                        ) {
                          e.currentTarget.style.color = linkStyleConfig.linkHover;
                        }
                      }}
                      onMouseLeave={e => {
                        if (linkStyleConfig.isButton && linkStyleConfig.style.backgroundColor) {
                          e.currentTarget.style.backgroundColor = linkStyleConfig.style
                            .backgroundColor as string;
                        } else if (
                          linkStyleConfig.linkColor &&
                          isHexColor(linkStyleConfig.linkColor)
                        ) {
                          e.currentTarget.style.color = linkStyleConfig.linkColor;
                        }
                      }}
                    >
                      {link.text}
                    </ButtonLink>
                  );
                })}
              </div>
            </div>

            {/* Mobile menu button - Always on the right */}
            <div className="md:hidden ml-auto">
              <button
                data-navbar-toggle=""
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`inline-flex items-center justify-center p-2 rounded-md ${!isHexColor(data.customColors?.linkText || themeStyles.navbar?.linkText || themeStyles.navbar?.text) ? data.customColors?.linkText || themeStyles.navbar?.linkText || themeStyles.navbar?.text : ''} hover:opacity-80 transition-opacity`}
                style={
                  isHexColor(
                    data.customColors?.linkText ||
                      themeStyles.navbar?.linkText ||
                      themeStyles.navbar?.text
                  )
                    ? {
                        color:
                          data.customColors?.linkText ||
                          themeStyles.navbar?.linkText ||
                          themeStyles.navbar?.text,
                      }
                    : {}
                }
                aria-expanded={isMobileMenuOpen ? 'true' : 'false'}
              >
                <span className="sr-only">
                  {isMobileMenuOpen ? 'Close main menu' : 'Open main menu'}
                </span>
                {isMobileMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu — always in the DOM so SSR output includes it;
            visibility is toggled via the "hidden" class for the navbar.js widget
            and via React state for in-editor interactivity. */}
          <div className={`md:hidden ${isMobileMenuOpen ? '' : 'hidden'}`} data-navbar-mobile="">
            <div
              className={`px-2 pt-2 pb-3 space-y-2 sm:px-3 ${
                !isHexColor(backgroundColor) ? backgroundColor : ''
              } ${themeStyles.navbar?.border || ''}`}
              style={isHexColor(backgroundColor) ? { backgroundColor: backgroundColor } : {}}
            >
              {sortedLinks.map((link, index) => {
                const linkStyleConfig = getLinkStyles(link.style || 'normal', true);
                const resolvedUrl = resolveNavbarLinkUrl(link, sortedLinks);
                const navUrl = getNavigationUrl(resolvedUrl);

                return (
                  <ButtonLink
                    key={index}
                    url={navUrl}
                    target={link.target}
                    className={linkStyleConfig.className}
                    style={{ ...linkStyleConfig.style, ...bodyFontStyle }}
                    onClick={() => setIsMobileMenuOpen(false)}
                    onMouseEnter={e => {
                      if (linkStyleConfig.isButton && linkStyleConfig.buttonHoverBg) {
                        if (isHexColor(linkStyleConfig.buttonHoverBg)) {
                          e.currentTarget.style.backgroundColor = linkStyleConfig.buttonHoverBg;
                        }
                      } else if (
                        isHexColor(linkStyleConfig.linkHover) &&
                        linkStyleConfig.linkHover
                      ) {
                        e.currentTarget.style.color = linkStyleConfig.linkHover;
                      }
                    }}
                    onMouseLeave={e => {
                      if (linkStyleConfig.isButton && linkStyleConfig.style.backgroundColor) {
                        e.currentTarget.style.backgroundColor = linkStyleConfig.style
                          .backgroundColor as string;
                      } else if (
                        linkStyleConfig.linkColor &&
                        isHexColor(linkStyleConfig.linkColor)
                      ) {
                        e.currentTarget.style.color = linkStyleConfig.linkColor;
                      }
                    }}
                  >
                    {link.text}
                  </ButtonLink>
                );
              })}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
